import { useCallback, useEffect, useRef, useState } from "react";
import {
  PencilSimple,
  Trash,
  Smiley,
  ArrowBendUpLeft,
  Quotes,
  PushPin,
  TerminalWindow,
} from "@/components/ui/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  TOOLBAR_EMOJI_BUTTON,
  ToolbarIconButton,
} from "@/components/ui/toolbar-icon-button";
import { Button } from "@/components/ui/button";

const QUICK_EMOJIS = ["👍", "👎", "❤️", "🎉", "👀", "🚀"];
/** Emojis in the "more" picker dropdown; must not duplicate QUICK_EMOJIS. */
const PICKER_EMOJIS = [
  "✅",
  "🤔",
  "😂",
  "🔥",
  "😮",
  "👏",
  "💯",
  "🙏",
  "✨",
  "💡",
];

interface MessageActionsProps {
  open?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onReact?: (emoji: string) => void;
  /** Discord-shape "Reply" action — always available on threadable
   *  messages, regardless of whether the row itself is an inline
   *  reply. Replies always cite the immediate clicked message; no
   *  "Open conversation" alternative. */
  onInlineReply?: () => void;
  /** Discord-shape "Create Thread" action — starts a fresh
   *  side-conversation rooted at this message. The mutator posts a
   *  system message in the channel announcing the thread, opens the
   *  sidebar. Hidden on rows that are already inside a thread. */
  onCreateThread?: () => void;
  onPin?: () => void;
  onSendToShell?: () => void;
  isOwn?: boolean;
  isPinned?: boolean;
  onLockOpen?: (locked: boolean) => void;
}

export function MessageActions({
  open,
  onEdit,
  onDelete,
  onReact,
  onInlineReply,
  onCreateThread,
  onPin,
  onSendToShell,
  isOwn,
  isPinned,
  onLockOpen,
}: MessageActionsProps) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const setEmojiPicker = useCallback(
    (value: boolean) => {
      setShowEmojiPicker(value);
      onLockOpen?.(value);
    },
    [onLockOpen],
  );

  useEffect(() => {
    if (!showEmojiPicker) return;
    const close = () => setEmojiPicker(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [showEmojiPicker, setEmojiPicker]);

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiPicker, setEmojiPicker]);

  return (
    <div ref={containerRef} className="absolute -top-3 right-2 z-10">
      {showEmojiPicker && (
        <div className="absolute bottom-full right-0 mb-1 flex gap-0.5 bg-card border border-border rounded-full shadow-md px-2 py-1 z-20">
          {PICKER_EMOJIS.map((emoji) => (
            <Button
              key={emoji}
              type="button"
              variant="ghost"
              onClick={() => {
                onReact?.(emoji);
              }}
              className={TOOLBAR_EMOJI_BUTTON}
            >
              {emoji}
            </Button>
          ))}
        </div>
      )}
      <div
        className={`transition-opacity duration-[var(--duration-normal)] ease-[var(--ease-default)] ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      >
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center gap-0.5 bg-card border border-border rounded-full shadow-sm px-2 py-1">
            {QUICK_EMOJIS.map((emoji) => (
              <Button
                key={emoji}
                type="button"
                variant="ghost"
                onClick={() => onReact?.(emoji)}
                className={TOOLBAR_EMOJI_BUTTON}
              >
                {emoji}
              </Button>
            ))}
            <Tooltip>
              <TooltipTrigger asChild>
                <ToolbarIconButton
                  onClick={() => setEmojiPicker(!showEmojiPicker)}
                  aria-label="More reactions"
                >
                  <Smiley
                    size={16}
                    weight="Bold"
                    className="text-muted-foreground"
                  />
                </ToolbarIconButton>
              </TooltipTrigger>
              <TooltipContent>More reactions</TooltipContent>
            </Tooltip>
            <div className="w-px h-4 bg-border mx-0.5" />
            {onInlineReply && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToolbarIconButton onClick={onInlineReply} aria-label="Reply">
                    <ArrowBendUpLeft
                      size={16}
                      weight="Bold"
                      className="text-muted-foreground"
                    />
                  </ToolbarIconButton>
                </TooltipTrigger>
                <TooltipContent>Reply</TooltipContent>
              </Tooltip>
            )}
            {onCreateThread && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToolbarIconButton
                    onClick={onCreateThread}
                    aria-label="Create thread"
                  >
                    <Quotes
                      size={16}
                      weight="Bold"
                      className="text-muted-foreground"
                    />
                  </ToolbarIconButton>
                </TooltipTrigger>
                <TooltipContent>Create thread</TooltipContent>
              </Tooltip>
            )}
            {onPin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToolbarIconButton
                    onClick={onPin}
                    aria-label={isPinned ? "Unpin message" : "Pin message"}
                  >
                    <PushPin
                      size={16}
                      weight="Bold"
                      className={
                        isPinned ? "text-warning" : "text-muted-foreground"
                      }
                    />
                  </ToolbarIconButton>
                </TooltipTrigger>
                <TooltipContent>
                  {isPinned ? "Unpin message" : "Pin message"}
                </TooltipContent>
              </Tooltip>
            )}
            {onSendToShell && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToolbarIconButton
                    onClick={onSendToShell}
                    aria-label="Send to shell"
                  >
                    <TerminalWindow
                      size={16}
                      weight="Bold"
                      className="text-muted-foreground"
                    />
                  </ToolbarIconButton>
                </TooltipTrigger>
                <TooltipContent>Send to shell</TooltipContent>
              </Tooltip>
            )}
            {isOwn && onEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToolbarIconButton onClick={onEdit} aria-label="Edit message">
                    <PencilSimple
                      size={16}
                      weight="Bold"
                      className="text-muted-foreground"
                    />
                  </ToolbarIconButton>
                </TooltipTrigger>
                <TooltipContent>Edit message</TooltipContent>
              </Tooltip>
            )}
            {isOwn && onDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ToolbarIconButton
                    onClick={onDelete}
                    aria-label="Delete message"
                  >
                    <Trash
                      size={16}
                      weight="Bold"
                      className="text-destructive/70"
                    />
                  </ToolbarIconButton>
                </TooltipTrigger>
                <TooltipContent>Delete message</TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
}
